import { memo, useCallback, useMemo, useState } from 'react';
import { Box, Flex, Text } from '@optiaxiom/react';
import { IconArrowsRotate } from '@optimizely/axiom-icons';
import { NodeProps, NodeResizer, useNodes } from '@xyflow/react';

import { ForLoopStep } from '@frontend/types/workflow-agent';

import { useDynamicForLoopSizing } from '../../../../hooks';
import { useNodeRenderer } from '../../../../hooks/useNodeRenderer';
import { NodeContainer } from '../../container/NodeContainer';
import { NodeBadges } from '../../node-parts/NodeBadges';

import * as styles from './ForLoopSubflow.css';

/**
 * Extended ForLoop data with execution-specific properties
 */
type ForLoopNodeData = ForLoopStep & {
  executionStatus?: 'pending' | 'running' | 'completed' | 'failed';
  isSelected?: boolean;
  stepExecution?: Record<string, unknown>;
  executionCount?: number;
};

export type ForLoopSubflowProps = NodeProps;

export const ForLoopSubflow = memo(({ id, data, selected }: ForLoopSubflowProps) => {
  const nodeData = data as ForLoopNodeData;
  const {
    handleMouseEnter,
    handleMouseLeave,
    hasErrors,
    isSourceNode,
    addonLeft,
    addonRight,
    addonBottom,
  } = useNodeRenderer({ id, type: 'for_loop', data: nodeData, selected });

  const nodes = useNodes();
  const [isDragOver, setIsDragOver] = useState(false);
  const { dimensions } = useDynamicForLoopSizing(id || '');

  // Get execution status for styling
  const executionStatus = nodeData?.executionStatus;
  const isExecutionSelected = Boolean(nodeData?.isSelected);

  // Memoize container style to prevent object recreation on every render
  const containerStyle = useMemo(
    () => ({
      width: dimensions?.width || 320,
      height: dimensions?.height || 188,
    }),
    [dimensions]
  );

  // Check if there are child nodes inside this forloop
  const hasChildNodes = useMemo(() => {
    if (!id) return false;
    return nodes.some(node => node.parentId === id);
  }, [id, nodes]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  return (
    <NodeContainer
      selected={selected}
      hasErrors={hasErrors}
      isSourceNode={isSourceNode}
      executionStatus={executionStatus}
      isExecutionSelected={isExecutionSelected}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={styles.forLoopContainer}
      style={containerStyle}
      dataAttributes={{ dragover: isDragOver, 'forloop-id': id || '' }}
      nodeId={id}
    >
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* ForLoop name in top left corner when there are child nodes */}
        {hasChildNodes && (
          <Box
            className={styles.forLoopNameBadge}
            style={{ display: 'flex', flexDirection: 'row' }}
          >
            <Box className={styles.iconContainer}>
              <IconArrowsRotate size="20" className={styles.loopIcon} />
            </Box>

            <Text className={styles.forLoopNameText}>{nodeData?.name || 'Loop step '}</Text>
          </Box>
        )}

        {/* Main content - hide when there are child nodes */}
        {!hasChildNodes && (
          <Flex flexDirection="column" alignItems="center" gap="8" className={styles.content}>
            {/* Loop icon */}
            <Box className={styles.iconContainer}>
              <IconArrowsRotate size="20" className={styles.loopIcon} />
            </Box>

            {/* Loop name */}
            <Text className={styles.loopName}>{nodeData?.name || 'Unnamed Loop'}</Text>
          </Flex>
        )}

        {!hasChildNodes && <Text className={styles.dropZoneText}>Drag and drop here</Text>}

        <NodeBadges addonLeft={addonLeft} addonRight={addonRight} addonBottom={addonBottom} />

        {/* NodeResizer for automatic sizing */}
        {selected && <NodeResizer minWidth={320} minHeight={188} isVisible={false} />}
      </Box>
    </NodeContainer>
  );
});

ForLoopSubflow.displayName = 'ForLoopSubflow';
